/** biome-ignore-all lint/a11y/useSemanticElements: legacy */
'use client';

import { useEffect, useState } from 'react';

import cn from 'classnames';
import { useAtomValue } from 'jotai';
import stableHash from 'stable-hash';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import type { LocationAttrs } from '@core/Entities/Location/LocationAttr.entity';
import { getFilterValueDisplayName } from '@core/Utils/SearchFilters/GetFilterValueDisplayName';
import { getFilterValueId } from '@core/Utils/SearchFilters/GetFilterValueId';
import { mapFiltersForDisplay } from '@core/Utils/SearchFilters/MapFiltersForDisplay';
import { getLabelWithEllipsis } from '@core/Utils/Strings/GetLabelWithEllipsis';
import Button from '@gds/Button/Button';
import { Chip } from '@gds/Chip/Chip';
import { dynamicFilterMessagesAtom } from '@plp/Atoms/DynamicFilterMessagesAtom';

import type { PlpFiltersMessagesProps } from '../../FilterPanel.entity';
import { DEFAULT_VISIBLE_NUM } from './ActiveFiltersOverview.constants';
import { useGetDeleteFn } from './UseGetDeleteFn';

import styles from './ActiveFiltersOverview.module.css';

interface ActiveFiltersOverviewProps {
  strings: PlpFiltersMessagesProps;
  noBottomBorder?: boolean;
  locationAttrs?: LocationAttrs;
}

export const ActiveFiltersOverview = ({
  strings,
  noBottomBorder,
  locationAttrs,
}: ActiveFiltersOverviewProps) => {
  const activeFilters = useAtomValue(filtersListAtom);
  const aggregations = useAtomValue(aggregationsAtom);
  const dynamicStrings = useAtomValue(dynamicFilterMessagesAtom);
  const displayFilters = mapFiltersForDisplay(activeFilters);

  const combinedStrings = { ...strings, ...dynamicStrings };

  const [willFiltersOverflow, setWillFiltersOverflow] = useState(
    displayFilters.length > DEFAULT_VISIBLE_NUM,
  );
  const [isTruncated, setIsTruncated] = useState(true);
  const { getDeleteFn } = useGetDeleteFn();

  useEffect(() => {
    setWillFiltersOverflow(displayFilters.length > DEFAULT_VISIBLE_NUM);
  }, [stableHash(displayFilters), DEFAULT_VISIBLE_NUM]);

  const displayLength = isTruncated ? DEFAULT_VISIBLE_NUM : displayFilters.length;
  const wrapperClassNames = cn(styles.activeFiltersWrapper, {
    [styles.activeFiltersPresent]: displayFilters.length > 0,
    [styles.noBorder]: noBottomBorder,
  });

  return (
    <div className={wrapperClassNames} role="list">
      {displayFilters.slice(0, displayLength).map(filter => {
        const deleteFn = getDeleteFn(filter);
        const valueId = getFilterValueId(filter);

        if (filter.id === 'postcode' && !filter.postcode) return null;

        const chipLabel = getFilterValueDisplayName(
          filter,
          combinedStrings,
          aggregations,
          locationAttrs,
        );
        return (
          <Chip
            key={`${filter.id}-${valueId}`}
            className={styles.filterChip}
            onDelete={deleteFn}
            variant="filter"
            id={`active-filter-overview-chip-${filter.id}-${valueId}`}
            dataTestId="active-filter-chip"
            role="listitem"
            deleteLabel={combinedStrings.overviewRemove}
          >
            {getLabelWithEllipsis(chipLabel!, 30, 27)}
          </Chip>
        );
      })}{' '}
      {willFiltersOverflow && (
        <Button
          className={styles.moreLessBtn}
          variant="link"
          size="small"
          dataTestId="toggle-all-active-filters"
          onClick={() => setIsTruncated(!isTruncated)}
        >
          {isTruncated
            ? `+${displayFilters.length - DEFAULT_VISIBLE_NUM} ${combinedStrings.overviewMore}`
            : combinedStrings.overviewLess}
        </Button>
      )}
    </div>
  );
};
